import { memo, ReactNode } from "react";
import {
    Stack,
    Spinner,
    Label,
    MessageBar,
    MessageBarType,
    Modal,
    Icon,
    mergeStyles
} from "@fluentui/react";
import { runIndexer } from "../../../api/admin/api_admin";

/**
 * 「編集を完了」で保留していた変更をまとめて実行するモーダル
 * (ApplyChangesModal / ApplyTagsModal)の共通部品
 */

const applyModalStyle = mergeStyles({
    padding: '24px',
    minWidth: '400px',
    width: '70vw',
    maxWidth: '1000px',

    minHeight: '200px',
    height: '65vh',
    maxHeight: '700px',

    display: 'flex',
    flexDirection: 'column',
})

/** モーダルの枠と見出し */
export const ApplyModalFrame = (props: { containerName: string, children: ReactNode }) => (
    <Modal isOpen isBlocking>
        <Stack className={applyModalStyle} tokens={{ childrenGap: 16 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                <Label styles={{ root: { fontSize: 16, fontWeight: 700 } }}>編集の完了</Label>
                <Label styles={{ root: { fontSize: 16, fontWeight: 700 } }}>＜{props.containerName}＞</Label>
            </Stack>
            {props.children}
        </Stack>
    </Modal>
)

const progressRowStyle = mergeStyles({
    padding: '8px 12px',
    borderRadius: 4,
    backgroundColor: "#f3f2f1",
    // 対象が数千件になっても表示領域外の行はレイアウト/描画を省略させる
    contentVisibility: 'auto',
    containIntrinsicSize: 'auto 40px',
})

/**
 * 1件ずつ実行する処理の対象を示す行。
 * 進捗を1件ずつ反映するため、状態が変わっていない行は再レンダリングしない
 */
export const ApplyProgressRow = memo((props: {
    name: string
    // 行の右側に出す補足(サイズや付与するサブカテゴリなど)
    detail: string
    // 未処理はundefined、成否をtrue/falseで受ける
    result: boolean | undefined
}) => {
    const { name, detail, result } = props
    const icon = result === undefined
        ? { iconName: "Page", color: "#605e5c" }
        : result
            ? { iconName: "CheckMark", color: "#107c10" }
            : { iconName: "ErrorBadge", color: "#d13438" }

    return (
        <Stack className={progressRowStyle} horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
            <div style={{ width: 20, textAlign: 'center', flexShrink: 0 }}>
                <Icon iconName={icon.iconName} styles={{ root: { color: icon.color, fontSize: 14 } }} />
            </div>

            <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                <div
                    style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={name}
                >
                    {name}
                </div>
            </Stack.Item>

            <div
                style={{
                    fontSize: 12, color: '#605e5c', flexShrink: 0, maxWidth: 300, textAlign: 'right',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}
                title={detail}
            >
                {detail}
            </div>
        </Stack>
    )
})

/**
 * itemsを1件ずつ順に実行する。1件の失敗で残りを止めないよう、例外も失敗として扱って次へ進む。
 * 進捗を画面に出せるよう、1件終わるごとにonProgressへキー単位の成否を渡す
 */
export const runSequentially = async <T,>(
    items: T[],
    keyOf: (item: T) => number,
    run: (item: T) => Promise<boolean>,
    onProgress: (results: Map<number, boolean>) => void
): Promise<{ succeeded: T[], failed: T[] }> => {
    const results = new Map<number, boolean>()
    const succeeded: T[] = []
    const failed: T[] = []
    for (const item of items) {
        let success = false
        try {
            success = await run(item)
        } catch (err) {
            success = false
        }
        if (success) {
            succeeded.push(item)
        } else {
            failed.push(item)
        }
        results.set(keyOf(item), success)
        // Mapは参照が変わらないと再レンダリングされないためコピーを渡す
        onProgress(new Map(results))
    }
    return { succeeded, failed }
}

export interface IndexerRunOutcome {
    succeeded: boolean
    // 文書検索が未設定で反映そのものが不要だった場合。失敗ではないので警告色にしない
    notConfigured: boolean
    message: string
}

/** 保留していた変更を実行し終えた後の、検索への反映(インデクサの実行) */
export const runIndexerForApply = async (containerSeq: number): Promise<IndexerRunOutcome> => {
    try {
        const result = await runIndexer({ container_seq: containerSeq })
        return {
            succeeded: result.success,
            notConfigured: result.notConfigured ?? false,
            message: result.message
        }
    } catch (err) {
        return {
            succeeded: false,
            notConfigured: false,
            message: "検索への反映中にエラーが発生しました。時間をおいて再度実行してください。"
        }
    }
}

/** 検索への反映中の表示 */
export const IndexingPane = () => (
    <Stack horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 10 }} styles={{ root: { flex: 1 } }}>
        <Spinner label="検索へ反映しています..." />
    </Stack>
)

/**
 * 完了時の結果メッセージ。
 * errorMessageは反映より前の段階で打ち切った場合に渡す(反映の結果より優先して出す)
 */
export const ApplyResultMessageBar = (props: { outcome: IndexerRunOutcome | null, errorMessage?: string | null }) => {
    const { outcome, errorMessage } = props
    const type = errorMessage != null ? MessageBarType.error
        : outcome === null ? MessageBarType.error
            : outcome.notConfigured ? MessageBarType.info
                : outcome.succeeded ? MessageBarType.success
                    : MessageBarType.error

    return (
        <MessageBar messageBarType={type}>
            {errorMessage ?? outcome?.message ?? ""}
        </MessageBar>
    )
}
